import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Plus, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  allergiesService,
  PatientAllergy,
  AllergyCategory,
  AllergyCriticality,
} from '@/services/allergiesService';
import { fetchAllergies, AllergyOption } from '@/services/allergyService';

interface PatientAllergyListTabProps {
  patientId: string;
  needsReconciliation?: boolean;
}

const PatientAllergyListTab = ({ patientId, needsReconciliation }: PatientAllergyListTabProps) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const canWrite = isPractitioner || isAdmin;

  const [allergies, setAllergies] = useState<PatientAllergy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<{
    substance: string;
    code: string;
    category: AllergyCategory;
    criticality: AllergyCriticality;
    reaction: string;
    note: string;
  }>({
    substance: '',
    code: '',
    category: 'medication',
    criticality: 'low',
    reaction: '',
    note: '',
  });
  const [substanceSuggestions, setSubstanceSuggestions] = useState<AllergyOption[]>([]);
  const [showSubstanceMenu, setShowSubstanceMenu] = useState(false);
  const substanceDebounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced typeahead — query NLM Clinical Tables API as the provider types.
  useEffect(() => {
    if (substanceDebounce.current) clearTimeout(substanceDebounce.current);
    substanceDebounce.current = setTimeout(async () => {
      if (!form.substance.trim()) {
        setSubstanceSuggestions([]);
        return;
      }
      try {
        const results = await fetchAllergies(form.substance);
        setSubstanceSuggestions(results.slice(0, 8));
      } catch {
        // network failures fall back to no suggestions; the manual input still works.
      }
    }, 200);
    return () => {
      if (substanceDebounce.current) clearTimeout(substanceDebounce.current);
    };
  }, [form.substance]);

  const refetch = async () => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const rows = await allergiesService.getPatientAllergies(clinic.id, patientId);
      setAllergies(rows);
    } catch (error: any) {
      toast({
        title: 'Failed to load allergies',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, clinic?.id]);

  const resetForm = () => setForm({
    substance: '',
    code: '',
    category: 'medication',
    criticality: 'low',
    reaction: '',
    note: '',
  });

  const handleAdd = async () => {
    if (!clinic?.id || !form.substance.trim()) return;
    setIsSaving(true);
    try {
      await allergiesService.createAllergy(clinic.id, patientId, {
        allergenCode: form.code.trim() || form.substance.trim().slice(0, 20),
        allergenDisplay: form.substance.trim(),
        codeSystem: form.code.trim() ? 'http://snomed.info/sct' : 'free-text',
        category: form.category,
        criticality: form.criticality,
        type: 'allergy',
        clinicalStatus: 'active',
        verificationStatus: 'confirmed',
        reactions: form.reaction.trim()
          ? [{ description: form.reaction.trim() }]
          : undefined,
        note: form.note.trim() || undefined,
      });
      toast({ title: 'Allergy recorded', variant: 'success' });
      setIsAddOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to add allergy',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResolve = async (allergyId: string) => {
    if (!clinic?.id) return;
    try {
      await allergiesService.resolveAllergy(clinic.id, allergyId);
      toast({ title: 'Allergy marked resolved', variant: 'success' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to resolve allergy',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const handleConfirm = async (allergy: PatientAllergy) => {
    if (!clinic?.id) return;
    try {
      await allergiesService.updateAllergy(clinic.id, allergy.id, {
        verificationStatus: 'confirmed',
      });
      toast({ title: 'Allergy confirmed', variant: 'success' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to confirm allergy',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const active = allergies.filter(a => a.clinical_status === 'active');
  const resolved = allergies.filter(a => a.clinical_status !== 'active');

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-orange-500">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Allergies</h3>
              <p className="text-xs text-slate-500">
                {isLoading ? 'Loading…' : `${active.length} active`}
              </p>
            </div>
          </div>
          {canWrite && (
            <Button size="sm" onClick={() => setIsAddOpen(true)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" />
              Add allergy
            </Button>
          )}
        </div>

        {needsReconciliation && (
          <div className="mx-5 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Allergies need reconciliation</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Some entries below were imported from the legacy allergy list and are marked
                unconfirmed. Review each one and click "Confirm" — or remove it if incorrect.
              </p>
            </div>
          </div>
        )}

        <div className="p-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            </div>
          ) : active.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No active allergies — NKDA</div>
          ) : (
            <div className="space-y-2">
              {active.map(a => (
                <AllergyRow
                  key={a.id}
                  allergy={a}
                  canWrite={canWrite}
                  onResolve={handleResolve}
                  onConfirm={handleConfirm}
                />
              ))}
            </div>
          )}

          {resolved.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Resolved / inactive
              </div>
              <div className="space-y-2">
                {resolved.map(a => (
                  <AllergyRow
                    key={a.id}
                    allergy={a}
                    canWrite={false}
                    muted
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Sheet open={isAddOpen} onOpenChange={setIsAddOpen}>
        <SheetContent className="w-full !max-w-[480px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-slate-200">
            <SheetTitle>Add Allergy</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <div className="space-y-1.5 relative">
              <Label htmlFor="substance">Substance</Label>
              <Input
                id="substance"
                placeholder="Start typing — e.g., Penicillin"
                value={form.substance}
                onChange={(e) => {
                  setForm(prev => ({ ...prev, substance: e.target.value }));
                  setShowSubstanceMenu(true);
                }}
                onFocus={() => setShowSubstanceMenu(true)}
                onBlur={() => setTimeout(() => setShowSubstanceMenu(false), 150)}
                disabled={isSaving}
                autoComplete="off"
              />
              {showSubstanceMenu && substanceSuggestions.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md">
                  {substanceSuggestions.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setForm(prev => ({ ...prev, substance: s.label }));
                        setShowSubstanceMenu(false);
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">SNOMED / RxNorm code (optional)</Label>
              <Input
                id="code"
                placeholder="e.g., 387406002"
                value={form.code}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v: AllergyCategory) => setForm(prev => ({ ...prev, category: v }))}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medication">Medication</SelectItem>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="environment">Environment</SelectItem>
                    <SelectItem value="biologic">Biologic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Criticality</Label>
                <Select
                  value={form.criticality}
                  onValueChange={(v: AllergyCriticality) => setForm(prev => ({ ...prev, criticality: v }))}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low risk</SelectItem>
                    <SelectItem value="high">High risk</SelectItem>
                    <SelectItem value="unable-to-assess">Unable to assess</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reaction">Reaction</Label>
              <Input
                id="reaction"
                placeholder="e.g., hives, anaphylaxis"
                value={form.reaction}
                onChange={(e) => setForm(prev => ({ ...prev, reaction: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note">Notes</Label>
              <textarea
                id="note"
                className="w-full min-h-[80px] rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!form.substance.trim() || isSaving}
              className="rounded-lg"
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              ) : 'Add'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const criticalityBadge = (c?: AllergyCriticality | null) => {
  switch (c) {
    case 'high':
      return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">High risk</span>;
    case 'low':
      return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">Low risk</span>;
    default:
      return null;
  }
};

const AllergyRow: React.FC<{
  allergy: PatientAllergy;
  canWrite: boolean;
  muted?: boolean;
  onResolve?: (id: string) => void;
  onConfirm?: (a: PatientAllergy) => void;
}> = ({ allergy, canWrite, muted, onResolve, onConfirm }) => {
  const isUnconfirmed = allergy.verification_status === 'unconfirmed';
  return (
    <div
      className={`border border-slate-200 rounded-lg p-3 ${muted ? 'bg-slate-50' : 'bg-white'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
              {allergy.code_display}
            </span>
            {criticalityBadge(allergy.criticality)}
            {isUnconfirmed && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                Unconfirmed
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
            {Array.isArray(allergy.category) && allergy.category[0] && (
              <span className="capitalize">{allergy.category[0]}</span>
            )}
            <span>· {allergy.clinical_status}</span>
            {allergy.onset_date_time && (
              <span>· onset {new Date(allergy.onset_date_time).toLocaleDateString()}</span>
            )}
          </div>
          {allergy.reactions && allergy.reactions.length > 0 && (
            <p className="mt-1 text-xs text-slate-600">
              Reactions: {allergy.reactions.map(r => r.description || 'reaction recorded').join(', ')}
            </p>
          )}
          {allergy.note && (
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">{allergy.note}</p>
          )}
        </div>
        {canWrite && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {isUnconfirmed && onConfirm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onConfirm(allergy)}
                className="h-8 px-2 text-xs"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Confirm
              </Button>
            )}
            {onResolve && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResolve(allergy.id)}
                className="h-8 px-2 text-xs"
              >
                Resolve
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientAllergyListTab;
